/**
 * Brand-v2 KG category glyphs — ported from planapus-lab's
 * CategoryGlyph.tsx + tokens.ts. Twelve categories, each owning a
 * distinct geometric silhouette + saturated-earthy color. Replaces
 * the previous emoji icons on each process card in the picker.
 *
 * Identity through silhouette + color, never texture: no strokes, no
 * gradients, no border-radius. The shapes mirror brand-v2's Bauhaus-
 * primitive vocabulary.
 *
 * Renders a string of inline SVG so it can drop into Ahmed's vanilla
 * template-literal rendering without React. Pair with the CSS
 * variables `--cat-<category>` declared in pilot.css.
 */

export type CategoryKey =
  | "formation" | "taxation" | "employment" | "facility"
  | "environmental" | "trade" | "insurance" | "ip"
  | "data" | "financial" | "certification" | "industry";

type ShapeName =
  | "square" | "bar" | "triangle-up" | "arch" | "circle"
  | "parallelogram" | "shield" | "plus" | "hexagon"
  | "diamond" | "ring" | "octagon";

const SHAPE: Record<CategoryKey, ShapeName> = {
  formation:     "square",
  taxation:      "bar",
  employment:    "triangle-up",
  facility:      "arch",
  environmental: "circle",
  trade:         "parallelogram",
  insurance:     "shield",
  ip:            "plus",
  data:          "hexagon",
  financial:     "diamond",
  certification: "ring",
  industry:      "octagon",
};

function isCategoryKey(s: string | undefined): s is CategoryKey {
  return !!s && Object.prototype.hasOwnProperty.call(SHAPE, s);
}

function shapeBody(shape: ShapeName, fill: string): string {
  switch (shape) {
    case "square":
      return `<rect x="3" y="3" width="18" height="18" fill="${fill}"/>`;
    case "bar":
      return `<rect x="2" y="9" width="20" height="6" fill="${fill}"/>`;
    case "triangle-up":
      return `<polygon points="12,2 22,21 2,21" fill="${fill}"/>`;
    case "arch":
      return `<path d="M 3 21 L 3 12 A 9 9 0 0 1 21 12 L 21 21 Z" fill="${fill}"/>`;
    case "circle":
      return `<circle cx="12" cy="12" r="9" fill="${fill}"/>`;
    case "parallelogram":
      return `<polygon points="7,4 22,4 17,20 2,20" fill="${fill}"/>`;
    case "shield":
      return `<path d="M 3 3 H 21 V 14 L 12 21 L 3 14 Z" fill="${fill}"/>`;
    case "plus":
      return `<path d="M 9 3 H 15 V 9 H 21 V 15 H 15 V 21 H 9 V 15 H 3 V 9 H 9 Z" fill="${fill}"/>`;
    case "hexagon":
      return `<polygon points="12,2 21,7 21,17 12,22 3,17 3,7" fill="${fill}"/>`;
    case "diamond":
      return `<polygon points="12,2 22,12 12,22 2,12" fill="${fill}"/>`;
    case "ring":
      return `<path d="M 12 3 A 9 9 0 1 0 12 21 A 9 9 0 1 0 12 3 Z M 12 8 A 4 4 0 1 1 12 16 A 4 4 0 1 1 12 8 Z" fill="${fill}" fill-rule="evenodd"/>`;
    case "octagon":
      return `<polygon points="8,3 16,3 21,8 21,16 16,21 8,21 3,16 3,8" fill="${fill}"/>`;
    default:
      return `<rect x="3" y="3" width="18" height="18" fill="${fill}"/>`;
  }
}

/**
 * Render an inline-SVG string for a category glyph. `size` is the
 * pixel side; the shape always uses the 24-unit viewBox.
 *
 * Falls back to a neutral square if the category is unknown so the
 * picker doesn't render blank.
 */
export function categoryGlyphSvg(
  category: CategoryKey | string | undefined,
  size = 24,
): string {
  const shape: ShapeName = isCategoryKey(category) ? SHAPE[category] : "square";
  const fill = category ? `var(--cat-${category})` : "var(--ink-faint)";
  return (
    `<svg width="${size}" height="${size}" viewBox="0 0 24 24" ` +
    `fill="none" aria-hidden="true" focusable="false">` +
    shapeBody(shape, fill) +
    `</svg>`
  );
}
